/**
 * GLAMRDiP shared pagination-dots primitive.
 * Joel 2026-04-29.
 *
 * One dot represents one VIEWPORT-SIZED PAGE of content, not one card.
 * Formula:
 *   dotCount = max(1, ceil(scrollWidth / clientWidth))
 *   activeIdx = round(scrollLeft / clientWidth), clamped
 *
 * That makes the active dot the LAST dot when scrolled to the end (no
 * stragglers to the right) and the FIRST when at the start. Multi-card
 * viewports get fewer dots than total cards, single-card viewports get
 * one dot per card — both correct.
 *
 *   window.GdCarouselDots({
 *     track:  <the scroll container>,
 *     host:   <the empty dots container>,
 *     dotClass: 'gd-bb__dot',  // optional
 *     activeClass: 'is-active', // optional (default)
 *     label:  'Ingredient',     // aria-label prefix (default 'Page')
 *   });
 *
 * Returns { rebuild, update } in case the consumer needs to force a
 * recompute after a content change.
 *
 * Recomputes on resize + ResizeObserver on the track so dot count
 * stays accurate across breakpoints + async content settles. Hides
 * the host entirely when content fits without overflow.
 */
(() => {
  'use strict';
  function GdCarouselDots(opts) {
    const track = opts && opts.track;
    const host = opts && opts.host;
    if (!track || !host) return { rebuild() {}, update() {} };
    const dotClass = opts.dotClass || 'gd-dot';
    const activeClass = opts.activeClass || 'is-active';
    const label = opts.label || 'Page';

    function build() {
      const overflow = track.scrollWidth - track.clientWidth > 4;
      if (!overflow) {
        host.innerHTML = '';
        host.hidden = true;
        return;
      }
      host.hidden = false;
      const count = Math.max(1, Math.ceil(track.scrollWidth / track.clientWidth));
      // Only rebuild DOM if count changed (resize may not change count).
      if (host.children.length !== count) {
        host.innerHTML = '';
        for (let i = 0; i < count; i++) {
          const d = document.createElement('button');
          d.type = 'button';
          d.className = dotClass;
          d.setAttribute('aria-label', `${label} ${i + 1}`);
          const idx = i;
          d.addEventListener('click', () => {
            const target = idx * track.clientWidth;
            const max = track.scrollWidth - track.clientWidth;
            track.scrollTo({ left: Math.min(max, target), behavior: 'smooth' });
          });
          host.appendChild(d);
        }
      }
      update();
    }

    function update() {
      if (host.hidden || host.children.length === 0) return;
      const max = host.children.length - 1;
      const idx = Math.max(0, Math.min(max, Math.round(track.scrollLeft / Math.max(1, track.clientWidth))));
      Array.prototype.forEach.call(host.children, (d, i) => d.classList.toggle(activeClass, i === idx));
    }

    build();
    track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', build);
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(build);
      ro.observe(track);
    }
    // Image-load can change scrollWidth — recompute after a tail tick.
    setTimeout(build, 400);
    window.requestAnimationFrame(build);

    return { rebuild: build, update };
  }
  window.GdCarouselDots = GdCarouselDots;
})();
